/**
 * AtriumLandingPage — Marketing site for the Atrium platform.
 *
 * Style mirrors heavenlyaisolutions.com/ems-overview.html:
 *   - Dark navy backdrop with subtle radial glow
 *   - Centered hero with kicker pill + serif headline + dual CTAs
 *   - 4-pillar grid
 *   - Numbered alternating feature sections (01–10)
 *   - Stats row
 *   - Closing CTA + footer
 *
 * Color palette swapped from EMS teal → Atrium light-blue family
 * (sky-400 #38BDF8, blue-500 #3B82F6, slate-950 backdrop).
 *
 * "Login to Atrium" header pill routes to `/login` (AuthGatePage).
 * "Request a Demo" buttons open the DemoRequestModal which posts to
 * /api/demo-requests → emails andresmocny@heavenlyaisolutions.com.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Map, Sparkles, Layers, ChefHat, Calculator, Search, Wand2, MessageCircle,
  Camera, ShieldCheck, ArrowRight, ChevronDown, Building2, Workflow,
  CalendarDays, BarChart3,
} from 'lucide-react';
import DemoRequestModal from '../components/DemoRequestModal';
import AtriumLogo from '../components/AtriumLogo';

const NAV = [
  { href: '#overview', label: 'Overview' },
  { href: '#features', label: 'Features' },
  { href: '#gallery', label: 'In Action' },
  { href: '#ai', label: 'AI' },
];

const PILLARS = [
  {
    icon: <Building2 className="w-5 h-5" />,
    title: 'Projects & Reconstructions',
    blurb: 'Pre-opening, rebuild, refurb, new construction — every phase orchestrated end to end.',
  },
  {
    icon: <Workflow className="w-5 h-5" />,
    title: 'Operations Management',
    blurb: 'Run the property after opening: housekeeping, F&B, spa, security, sales — all in one canvas.',
  },
  {
    icon: <Calculator className="w-5 h-5" />,
    title: 'Financial Center',
    blurb: 'Budgets, targets, line-item builders, and AI document analysis from invoices to forecasts.',
  },
  {
    icon: <Sparkles className="w-5 h-5" />,
    title: 'AI-Powered Intelligence',
    blurb: 'ChatGPT, Gemini, and Claude — pick the model that fits the task. Atrium uses them all.',
  },
];

const FEATURES = [
  {
    n: '01', kicker: 'Visual Property Intelligence', icon: <Map className="w-5 h-5" />,
    title: 'Interactive Property Maps',
    body: 'Click anywhere on your property and see the truth — before / after renders, hotspot status, owner, ROI projection, and progress. Build maps for any property: a single tower, a sprawling resort, an entire portfolio.',
    bullets: [
      'Stacked full-width maps with movable, clickable hotspots',
      'Color-coded status widgets (Reconstruction · Crucial · Requested)',
      'Hover cards with ROI, completion %, and live photo gallery',
      'AI-generated reconstruction renders embedded into each area',
    ],
    visual: 'map',
    screenshot: '/atrium-shots/06-map-rooftop.jpg',
  },
  {
    n: '02', kicker: 'Project Orchestration', icon: <CalendarDays className="w-5 h-5" />,
    title: 'Roadmaps, Phases & Multi-Day Calendar',
    body: 'A horizontal 6-phase Hyatt-style roadmap, a vertical 30-milestone timeline, and a public calendar that spans multi-day events with phase-tinted bars. Drag-and-drop reordering and inline date edits.',
    bullets: [
      '30 milestones from demolition → grand opening',
      'Color-coded phase tracking (Strategic Planning → Post-Opening)',
      'Multi-day event spans with progress fills',
      'One-click sync from roadmap → calendar so nothing drifts',
    ],
    visual: 'roadmap',
    screenshot: '/atrium-shots/09-timeline.jpg',
  },
  {
    n: '03', kicker: 'Departmental Workspaces', icon: <Layers className="w-5 h-5" />,
    title: 'One Hub per Department, Powered by AI',
    body: 'Housekeeping, Front Desk, Spa, Security, Entertainment, Water Sports, Sales, Laundry, F&B Culinary, FOH — every department gets its own canvas with rosters, AI Schedules, and Roadmaps assigned to real people.',
    bullets: [
      'AI weekly schedules driven by occupancy & restaurant bookings',
      'Matrix view: staff × days × hours, exported to PDF, Excel, Word',
      'Departmental Roadmaps with task assignees and email reminders',
      'Form & list snapshots saved per-department (frozen versions)',
    ],
    visual: 'departments',
    screenshot: '/atrium-shots/12-schedule-matrix.jpg',
  },
  {
    n: '04', kicker: 'F&B Subsystem', icon: <ChefHat className="w-5 h-5" />,
    title: '11 Restaurant Concepts, Editable Live',
    body: 'Every outlet on your property — restaurant, bar, lobby café — gets a dedicated page with hero carousel, generated logo, editable menus, team roster, AI schedule, and per-restaurant roadmap. Mirror it on the public landing page in one click.',
    bullets: [
      'AI-generated brand logos via Gemini Nano Banana',
      'Upload menus (PDF, image) — instantly visible to guests',
      'Per-restaurant AI schedules tied to expected covers',
      'Public + internal views stay in perfect sync',
    ],
    visual: 'fnb',
    screenshot: '/atrium-shots/10-restaurant-dialog.jpg',
  },
  {
    n: '05', kicker: 'Financial Center', icon: <BarChart3 className="w-5 h-5" />,
    title: 'AI-Powered Budgets & Targets',
    body: 'Build owner-grade budgets line-by-line, set category targets, track every dollar against actuals. Upload an invoice, contract, or vendor quote — Atrium reads it, categorizes it, and proposes line items.',
    bullets: [
      'AI line-item builder seeded from a single prompt',
      'PDF / Excel / image document analysis (auto-categorization)',
      'Category targets vs. actuals with variance alerts',
      'Hyatt-branded export decks ready for owner presentations',
    ],
    visual: 'finance',
    screenshot: '/atrium-shots/02-property-details.jpg',
  },
  {
    n: '06', kicker: 'Natural Language Search', icon: <Search className="w-5 h-5" />,
    title: 'Project Search That Speaks Your Language',
    body: '"Show me everything tied to the Atrium repair this quarter." "List rooms missing FF&E." Atrium parses natural language and returns Apple-style cards across areas, milestones, restaurants, files, and team members.',
    bullets: [
      'Cross-entity search (areas, milestones, files, restaurants, team)',
      'Apple-style result cards with quick-jump deep links',
      'AI-powered relevance ranking — no boolean syntax required',
      'Works without an account — public search index for guest mode',
    ],
    visual: 'search',
    screenshot: '/atrium-shots/15-admin-areas.jpg',
  },
  {
    n: '07', kicker: 'Design & Render Studio', icon: <Wand2 className="w-5 h-5" />,
    title: 'AI Renders & Brand Standards',
    body: 'Generate photorealistic post-reconstruction renders for any area in seconds. Apply your brand standards across the property — Atrium reads your palette, fonts, and tone from the settings tab and applies them everywhere.',
    bullets: [
      'GPT Image 1 for high-fidelity render generation',
      'Gemini Nano Banana for fast iteration & enhancement',
      'AI Brand Standards generator (palette + voice + usage rules)',
      'Send any render straight back to its origin area',
    ],
    visual: 'renders',
  },
  {
    n: '08', kicker: 'AI Assistant', icon: <MessageCircle className="w-5 h-5" />,
    title: 'Heavenly Assistant — Always One Click Away',
    body: 'A floating AI co-pilot embedded in every screen. Ask "What\'s overdue this week?" or "Draft an owner update for the Atrium phase." Powered by your chosen model (ChatGPT, Gemini, or Claude).',
    bullets: [
      'Full context awareness across maps, roadmaps, finances',
      'Cloud-TTS narration (OpenAI) for guided walkthroughs',
      'Flying tutorial overlay for first-time users',
      'BYO API key — burn your own credits at scale if needed',
    ],
    visual: 'assistant',
  },
  {
    n: '09', kicker: 'OS&E Digitization', icon: <Camera className="w-5 h-5" />,
    title: 'Snap a Physical List, Get a Spreadsheet',
    body: 'Hand-written FF&E lists. Vendor invoices. Stock-take sheets. Point your phone at any list and Atrium reads it line-by-line into a structured inventory row — quantities, brands, vendor refs, all parsed.',
    bullets: [
      'Camera OCR with intelligent column mapping',
      'Bulk import: 50 rows in under 10 seconds',
      'AI deep research on any item (vendors, lead time, freight)',
      'Photos, currency, PO numbers — all editable inline',
    ],
    visual: 'ose',
  },
  {
    n: '10', kicker: 'Permissions & Workflow', icon: <ShieldCheck className="w-5 h-5" />,
    title: 'Role-Based Access + Change-Request Approvals',
    body: 'Strict RBAC across Viewer / Editor / Engineer / Admin. Editors propose changes — admins approve in a single click. Every change is logged, reversible, and auditable. Brute-force protected. JWT-secured.',
    bullets: [
      '4-tier role model + per-branch lockable workspaces',
      'Editor change requests with one-click admin approval',
      'Email notifications on every approval / rejection',
      'Locked branches: HR, Finance, OS&E, Property Budgets, IT',
    ],
    visual: 'rbac',
    screenshot: '/atrium-shots/14-admin-planning.jpg',
  },
];

/** Curated screenshots for the "Atrium in Action" gallery — high-impact
 *  shots showing the platform doing real work on a live property. */
const GALLERY = [
  { src: '/atrium-shots/01-hero-resort.jpg', caption: 'Branded property landing page', tag: 'Public site' },
  { src: '/atrium-shots/06-map-rooftop.jpg', caption: 'Interactive property maps with live hotspots', tag: 'Interactive maps' },
  { src: '/atrium-shots/03-fnb-concepts.jpg', caption: 'F&B subsystem — 11 outlets per property', tag: 'F&B' },
  { src: '/atrium-shots/09-timeline.jpg', caption: '30-milestone reconstruction timeline', tag: 'Roadmaps' },
  { src: '/atrium-shots/14-admin-planning.jpg', caption: 'Admin planning with drag-drop reordering', tag: 'Admin CMS' },
  { src: '/atrium-shots/12-schedule-matrix.jpg', caption: 'AI-generated weekly schedule matrix', tag: 'AI schedules' },
];

const AI_ENGINES = [
  { name: 'ChatGPT', model: 'GPT-5.2', color: '#10A37F', tagline: 'Strategy, planning, writing, deep analysis.' },
  { name: 'Gemini', model: '3 Flash · Nano Banana', color: '#4285F4', tagline: 'Vision, render generation, fast iteration.' },
  { name: 'Claude', model: 'Sonnet 4.5', color: '#D97706', tagline: 'Long-form reasoning, document review, code.' },
];

const STATS = [
  { value: '11', label: 'Departments' },
  { value: '3', label: 'AI Engines' },
  { value: '∞', label: 'Properties' },
  { value: '24/7', label: 'Insight' },
];

export default function AtriumLandingPage() {
  const navigate = useNavigate();
  const [demoOpen, setDemoOpen] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goLogin = () => navigate('/login');

  return (
    <div
      data-testid="atrium-landing"
      className="min-h-screen relative"
      style={{
        background: '#02060F',
        color: '#F8FAFC',
        fontFamily: 'Manrope, system-ui, sans-serif',
      }}
    >
      {/* Ambient radial glow — sits behind everything */}
      <div aria-hidden className="pointer-events-none fixed inset-0" style={{
        background: 'radial-gradient(80% 60% at 50% 0%, rgba(56,189,248,0.18) 0%, rgba(56,189,248,0.04) 30%, transparent 60%)',
        zIndex: 0,
      }} />

      {/* ─── TOP NAV ─── */}
      <nav
        data-testid="atrium-nav"
        className="fixed top-0 left-0 right-0 z-50 transition-all"
        style={{
          background: navScrolled ? 'rgba(2, 6, 15, 0.78)' : 'transparent',
          backdropFilter: navScrolled ? 'blur(16px)' : 'none',
          borderBottom: navScrolled ? '1px solid rgba(125,211,252,0.10)' : '1px solid transparent',
        }}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-3" data-testid="atrium-logo">
            {/* The same Atrium brand mark used inside the app — keeps the
                marketing site and the in-app header visually unified. Light-blue
                accent so it pops against the dark navy nav backdrop. */}
            <AtriumLogo variant="inline" size={28} accent="#7DD3FC" text="#FFFFFF" testId="atrium-brand-mark" />
            <span className="text-[10px] tracking-[0.2em] uppercase hidden sm:inline" style={{ color: '#7DD3FC' }}>by Heavenly AI</span>
          </a>

          <div className="hidden md:flex items-center gap-7">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} className="text-xs tracking-wide transition-colors hover:text-white" style={{ color: '#94A3B8' }}>
                {n.label}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setDemoOpen(true)}
              data-testid="nav-demo-btn"
              className="hidden sm:inline-flex px-4 py-2 rounded-full text-xs font-bold transition-all hover:-translate-y-0.5"
              style={{ border: '1px solid rgba(255,255,255,0.18)', color: 'white' }}
            >
              Request Demo
            </button>
            <button
              type="button"
              onClick={goLogin}
              data-testid="nav-login-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all hover:-translate-y-0.5"
              style={{
                background: 'linear-gradient(135deg, #38BDF8 0%, #2563EB 100%)',
                color: 'white',
                boxShadow: '0 4px 18px rgba(56,189,248,0.45)',
              }}
            >
              Login to Atrium <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      </nav>

      {/* ─── HERO ─── */}
      <section id="top" className="relative min-h-screen flex items-center justify-center px-5 pt-24 pb-20" style={{ zIndex: 1 }}>
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-8 animate-fade-in"
            style={{ background: 'rgba(56,189,248,0.10)', border: '1px solid rgba(56,189,248,0.30)' }} data-testid="hero-kicker">
            <Sparkles className="w-3 h-3" style={{ color: '#7DD3FC' }} />
            <span className="text-[10px] font-bold tracking-[0.25em] uppercase" style={{ color: '#7DD3FC' }}>
              The all-in-one hospitality operations platform
            </span>
          </div>

          <h1 className="font-medium tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(4rem, 12vw, 8rem)', lineHeight: 1, color: 'white' }}>
            Atrium<sup style={{ fontSize: '0.32em', fontFamily: 'Manrope', fontWeight: 600, color: '#7DD3FC', verticalAlign: 'super', marginLeft: 6 }}>™</sup>
          </h1>

          <p className="mt-4 text-xl md:text-2xl tracking-wide" style={{ color: '#CBD5E1', fontFamily: 'Manrope', letterSpacing: '0.15em' }}>
            OPERATIONS · PROJECTS · INTELLIGENCE
          </p>

          <p className="mt-8 max-w-2xl mx-auto text-base md:text-lg leading-relaxed italic" style={{ color: '#7DD3FC', fontFamily: "'Cormorant Garamond', serif" }}>
            "One command center for the whole property lifecycle — plan the build, run the open, and master every single day after."
          </p>

          {/* Visibility-for-leadership paragraph — placed up high in the hero so
              the audience (owners, VPs, asset managers, multi-property directors)
              sees themselves named within seconds of landing. Key roles get the
              light-blue accent to draw the eye. iter-95: emphasises "command
              center" framing and gives equal weight to ongoing operations
              alongside pre-opening / reconstruction. */}
          <p className="mt-7 max-w-3xl mx-auto text-base md:text-lg leading-relaxed" style={{ color: '#CBD5E1' }} data-testid="hero-visibility-copy">
            A command center for{' '}
            <strong style={{ color: '#7DD3FC' }}>owners</strong>,{' '}
            <strong style={{ color: '#7DD3FC' }}>VPs</strong>,{' '}
            <strong style={{ color: '#7DD3FC' }}>asset managers</strong>, and{' '}
            <strong style={{ color: '#7DD3FC' }}>multi-property directors</strong> — Atrium turns the daily reality of every property in your portfolio into a single, glanceable picture. Every project, every operational process, every department — surfaced in real time, presented{' '}
            <em style={{ color: '#F8FAFC' }}>simply</em>,{' '}
            <em style={{ color: '#F8FAFC' }}>visually</em>, and with surgical precision,
            and complemented by AI on every screen.
          </p>

          <p className="mt-6 max-w-2xl mx-auto text-sm md:text-base leading-relaxed" style={{ color: '#94A3B8' }}>
            Built for the <strong style={{ color: '#7DD3FC' }}>full property lifecycle</strong> — the everyday operations of an open hotel, the pre-opening of a new build, and every reconstruction or renovation in between. Fully powered by your choice of <strong style={{ color: '#7DD3FC' }}>ChatGPT</strong>,{' '}
            <strong style={{ color: '#7DD3FC' }}>Gemini</strong>, or{' '}
            <strong style={{ color: '#7DD3FC' }}>Claude</strong>.
          </p>

          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="#features"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-bold transition-all hover:-translate-y-0.5"
              style={{ background: 'white', color: '#0F172A' }}
              data-testid="hero-explore-btn"
            >
              Explore Features <ChevronDown className="w-4 h-4" />
            </a>
            <button
              type="button"
              onClick={() => setDemoOpen(true)}
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-bold transition-all hover:-translate-y-0.5"
              style={{
                background: 'linear-gradient(135deg, #38BDF8 0%, #2563EB 100%)',
                color: 'white',
                boxShadow: '0 12px 40px rgba(56,189,248,0.45)',
              }}
              data-testid="hero-demo-btn"
            >
              Request a Demo <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* In-hero device preview — single high-impact app screenshot
              behind/below the CTAs that grounds the abstract "Atrium™"
              headline with a real artifact. Lazy-loaded so it doesn't
              block the hero's first paint. */}
          <div className="mt-16 max-w-4xl mx-auto" data-testid="hero-device-preview">
            <div className="relative rounded-2xl overflow-hidden"
              style={{
                background: 'linear-gradient(180deg, #0B1830 0%, #06101F 100%)',
                border: '1px solid rgba(125,211,252,0.20)',
                boxShadow: '0 32px 120px rgba(56,189,248,0.25), inset 0 1px 0 rgba(255,255,255,0.05)',
              }}>
              <div className="px-4 py-2.5 flex items-center gap-1.5" style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(125,211,252,0.10)' }}>
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#475569' }} />
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#475569' }} />
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#475569' }} />
                <span className="ml-3 text-[10px] tracking-widest uppercase font-bold" style={{ color: '#7DD3FC', fontFamily: 'Manrope' }}>
                  atrium · dreams rose hall · live
                </span>
              </div>
              <img
                src="/atrium-shots/01-hero-resort.jpg"
                alt="Atrium platform — Dreams Rose Hall public site"
                loading="lazy"
                className="w-full h-auto block"
              />
            </div>
          </div>

          <div className="mt-16 animate-bounce-slow" style={{ color: 'rgba(125,211,252,0.5)' }}>
            <ChevronDown className="w-6 h-6 mx-auto" />
          </div>
        </div>
      </section>

      {/* ─── 4 PILLARS ─── */}
      <section id="overview" className="relative py-24 px-5 md:px-8" style={{ zIndex: 1 }}>
        <div className="max-w-7xl mx-auto">
          <SectionHeader kicker="Platform Overview" title="Four pillars. One operating system." />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
            {PILLARS.map((p, i) => (
              <div
                key={p.title}
                data-testid={`pillar-${i}`}
                className="group rounded-2xl p-6 transition-all hover:-translate-y-1"
                style={{
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)',
                  border: '1px solid rgba(125,211,252,0.12)',
                  backdropFilter: 'blur(8px)',
                }}
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 transition-all group-hover:scale-110"
                  style={{ background: 'linear-gradient(135deg, rgba(56,189,248,0.18) 0%, rgba(37,99,235,0.08) 100%)', color: '#7DD3FC' }}>
                  {p.icon}
                </div>
                <h3 className="text-lg font-bold mb-2" style={{ color: 'white' }}>{p.title}</h3>
                <p className="text-xs leading-relaxed" style={{ color: '#94A3B8' }}>{p.blurb}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FEATURE SECTIONS (numbered alternating layouts) ─── */}
      <section id="features" className="relative py-12 px-5 md:px-8" style={{ zIndex: 1 }}>
        <div className="max-w-7xl mx-auto space-y-32">
          {FEATURES.map((f, i) => (
            <FeatureRow key={f.n} feature={f} flip={i % 2 === 1} />
          ))}
        </div>
      </section>

      {/* ─── ATRIUM IN ACTION GALLERY ─── */}
      <section id="gallery" className="relative py-32 px-5 md:px-8" style={{ zIndex: 1 }}>
        <div className="max-w-7xl mx-auto">
          <SectionHeader kicker="Atrium in Action" title="One example property — same platform, any portfolio." />
          <p className="text-center mt-4 max-w-2xl mx-auto text-sm" style={{ color: '#94A3B8' }}>
            Every shot below is a live screen from <strong style={{ color: '#7DD3FC' }}>Dreams Rose Hall Resort &amp; Spa</strong> — a 495-key Hyatt property used here as an example deployment. Atrium runs identical command-center workflows for any open hotel, pre-opening, or renovation across your portfolio.
          </p>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {GALLERY.map((g, i) => (
              <GalleryCard key={g.src} item={g} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ─── AI ENGINES ─── */}
      <section id="ai" className="relative py-32 px-5 md:px-8" style={{ zIndex: 1 }}>
        <div className="max-w-6xl mx-auto">
          <SectionHeader kicker="AI Intelligence" title="Pick your engine. Or use all three." />
          <p className="text-center mt-4 max-w-2xl mx-auto text-sm" style={{ color: '#94A3B8' }}>
            Atrium ships natively integrated with the three leading frontier models.
            Pick the one that fits the task — or let Atrium pick for you. Bring your own
            API key for premium tiers, or use the included Heavenly key.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-12">
            {AI_ENGINES.map((eng, i) => (
              <div
                key={eng.name}
                data-testid={`ai-engine-${i}`}
                className="rounded-2xl p-7 transition-all hover:-translate-y-1 relative overflow-hidden"
                style={{
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.01) 100%)',
                  border: '1px solid rgba(125,211,252,0.15)',
                }}
              >
                <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full opacity-30 blur-3xl" style={{ background: eng.color }} />
                <div className="relative">
                  <span className="inline-block w-10 h-10 rounded-xl mb-4" style={{ background: eng.color, boxShadow: `0 8px 32px ${eng.color}50` }} />
                  <h3 className="text-2xl font-bold mb-1" style={{ color: 'white' }}>{eng.name}</h3>
                  <p className="text-xs font-bold tracking-wider uppercase mb-3" style={{ color: '#7DD3FC' }}>{eng.model}</p>
                  <p className="text-sm leading-relaxed" style={{ color: '#CBD5E1' }}>{eng.tagline}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── STATS ─── */}
      <section className="relative py-20 px-5" style={{ zIndex: 1 }}>
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {STATS.map((s) => (
            <div key={s.label} data-testid={`stat-${s.label.toLowerCase()}`}>
              <div className="text-5xl md:text-6xl font-medium" style={{ fontFamily: "'Cormorant Garamond', serif", color: 'white' }}>
                {s.value}
              </div>
              <p className="mt-2 text-[10px] tracking-[0.25em] uppercase font-bold" style={{ color: '#7DD3FC' }}>{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── INTEGRATIONS ─── */}
      <section id="integrations" className="relative py-24 px-5 md:px-8" style={{ zIndex: 1 }}>
        <div className="max-w-5xl mx-auto text-center">
          <SectionHeader kicker="Seamless Integration" title="Connected to the tools you already run on." />
          <div className="mt-12 flex flex-wrap items-center justify-center gap-3">
            {['OpenAI', 'Google Gemini', 'Anthropic Claude', 'Stripe', 'Resend', 'MongoDB', 'AutoCAD', 'Opera PMS', 'WhatsApp', 'Gmail'].map((name) => (
              <span key={name} className="px-5 py-2.5 rounded-full text-xs font-bold tracking-wide transition-all hover:-translate-y-0.5"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(125,211,252,0.15)', color: '#CBD5E1' }}>
                {name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CLOSING CTA ─── */}
      <section className="relative py-32 px-5" style={{ zIndex: 1 }}>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-medium tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(2.5rem, 7vw, 4.5rem)', lineHeight: 1.05, color: 'white' }}>
            Run your property<br />through one Atrium.
          </h2>
          <p className="mt-6 text-sm md:text-base leading-relaxed" style={{ color: '#94A3B8' }}>
            See how Atrium turns reconstructions, openings, and daily operations into a single
            living workspace. A personal walkthrough with Andres Mocny is one click away.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => setDemoOpen(true)}
              data-testid="cta-demo-btn"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-bold transition-all hover:-translate-y-0.5"
              style={{
                background: 'linear-gradient(135deg, #38BDF8 0%, #2563EB 100%)',
                color: 'white',
                boxShadow: '0 12px 40px rgba(56,189,248,0.45)',
              }}
            >
              Request a Demo <ArrowRight className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={goLogin}
              data-testid="cta-login-btn"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-bold transition-all hover:-translate-y-0.5"
              style={{ border: '1px solid rgba(255,255,255,0.20)', color: 'white' }}
            >
              Login to Atrium
            </button>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="relative py-12 px-5 md:px-8" style={{ zIndex: 1, borderTop: '1px solid rgba(125,211,252,0.10)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs" style={{ color: '#64748B' }}>
              © 2026 Atrium™ — All rights reserved.
            </p>
            <div className="flex items-center gap-5 text-xs" style={{ color: '#94A3B8' }}>
              <a href="https://heavenlyaisolutions.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                Heavenly AI Solutions
              </a>
              <a
                href="/security-features.html"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-security-link"
                className="hover:text-white transition-colors"
                title="Cybersecurity features & controls (Hyatt-grade)"
              >
                Security
              </a>
              <button type="button" onClick={() => setDemoOpen(true)} className="hover:text-white transition-colors">Contact</button>
              <button type="button" onClick={goLogin} className="hover:text-white transition-colors">Login</button>
            </div>
          </div>

          {/* Developer credit — matches the in-app footer attribution so the
              brand line stays identical across the marketing site and the
              live platform. */}
          <div
            className="mt-8 pt-6 flex flex-col sm:flex-row items-center justify-center gap-3 text-center"
            style={{ borderTop: '1px solid rgba(125,211,252,0.10)' }}
            data-testid="footer-developer-credit"
          >
            <img
              src="/logo-heavenly-ai.png"
              alt="Heavenly AI Solutions"
              className="h-10 w-auto object-contain opacity-95"
              style={{ filter: 'drop-shadow(0 4px 12px rgba(56,189,248,0.20))' }}
            />
            <p className="text-[11px]" style={{ fontFamily: 'Manrope, sans-serif', color: '#94A3B8' }}>
              Platform developed by{' '}
              <a
                href="https://heavenlyaisolutions.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#7DD3FC', fontWeight: 700 }}
                className="hover:underline"
              >
                Heavenly AI Solutions
              </a>
              <span className="mx-1.5" style={{ color: '#7DD3FC' }}>·</span>
              led by <span style={{ color: '#F8FAFC', fontWeight: 700 }}>Andres Mocny</span>
            </p>
          </div>
        </div>
      </footer>

      <DemoRequestModal open={demoOpen} onClose={() => setDemoOpen(false)} />

      <style>{`
        @keyframes fade-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in { animation: fade-in 0.8s ease-out both; }
        @keyframes bounce-slow { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(8px); } }
        .animate-bounce-slow { animation: bounce-slow 2.4s ease-in-out infinite; }
      `}</style>
    </div>
  );
}

/* ──────────────────────────  Helpers  ────────────────────────── */

function SectionHeader({ kicker, title }) {
  return (
    <div className="text-center">
      <p className="text-[10px] font-bold tracking-[0.30em] uppercase" style={{ color: '#7DD3FC' }}>{kicker}</p>
      <h2 className="mt-3 text-4xl md:text-5xl font-medium tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif", color: 'white' }}>
        {title}
      </h2>
    </div>
  );
}

function FeatureRow({ feature, flip }) {
  return (
    <div data-testid={`feature-${feature.n}`} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${flip ? 'lg:[direction:rtl]' : ''}`}>
      <div className={flip ? 'lg:[direction:ltr]' : ''}>
        <div className="relative">
          <span className="absolute -top-8 -left-2 text-7xl font-medium opacity-10 select-none"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: 'white' }}>
            {feature.n}
          </span>
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded-lg flex items-center justify-center"
                style={{ background: 'rgba(56,189,248,0.12)', border: '1px solid rgba(56,189,248,0.25)', color: '#7DD3FC' }}>
                {feature.icon}
              </span>
              <p className="text-[10px] font-bold tracking-[0.25em] uppercase" style={{ color: '#7DD3FC' }}>{feature.kicker}</p>
            </div>
            <h3 className="text-3xl md:text-4xl font-medium leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", color: 'white' }}>
              {feature.title}
            </h3>
            <p className="mt-5 text-sm leading-relaxed" style={{ color: '#CBD5E1' }}>{feature.body}</p>
            <ul className="mt-6 space-y-2.5">
              {feature.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-xs" style={{ color: '#94A3B8' }}>
                  <span className="mt-1.5 w-1 h-1 rounded-full flex-shrink-0" style={{ background: '#38BDF8' }} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <div className={flip ? 'lg:[direction:ltr]' : ''}>
        {feature.screenshot ? (
          <BrowserFrame src={feature.screenshot} alt={feature.title} />
        ) : (
          <FeatureVisual kind={feature.visual} />
        )}
      </div>
    </div>
  );
}

/** Browser-chrome frame around a real Atrium app screenshot.
 *  Reuses the same chrome as the CSS mockups so the screenshots feel
 *  like part of the same visual language. */
function BrowserFrame({ src, alt }) {
  return (
    <div className="relative rounded-2xl overflow-hidden transition-transform hover:-translate-y-1 hover:rotate-[0.3deg]"
      style={{
        background: 'linear-gradient(180deg, #0B1830 0%, #06101F 100%)',
        border: '1px solid rgba(125,211,252,0.18)',
        boxShadow: '0 24px 80px rgba(56,189,248,0.18), inset 0 1px 0 rgba(255,255,255,0.04)',
      }}>
      <div className="px-4 py-2.5 flex items-center gap-1.5" style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(125,211,252,0.10)' }}>
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#475569' }} />
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#475569' }} />
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#475569' }} />
        <span className="ml-3 text-[10px] tracking-widest uppercase font-bold" style={{ color: '#7DD3FC', fontFamily: 'Manrope' }}>
          atrium · live
        </span>
      </div>
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="w-full h-auto block"
        style={{ aspectRatio: '16/10', objectFit: 'cover', objectPosition: 'top center' }}
      />
    </div>
  );
}

/** Decorative CSS-only "screenshot" mockups for each feature.
 *  These convey functionality without bundling actual screenshots — fast,
 *  themeable, and infinitely lighter than image assets. */
function FeatureVisual({ kind }) {
  const Frame = ({ children }) => (
    <div className="relative rounded-2xl overflow-hidden transition-transform hover:-translate-y-1 hover:rotate-[0.3deg]"
      style={{
        background: 'linear-gradient(180deg, #0B1830 0%, #06101F 100%)',
        border: '1px solid rgba(125,211,252,0.18)',
        boxShadow: '0 24px 80px rgba(56,189,248,0.12), inset 0 1px 0 rgba(255,255,255,0.04)',
      }}>
      <div className="px-4 py-2.5 flex items-center gap-1.5" style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(125,211,252,0.10)' }}>
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#475569' }} />
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#475569' }} />
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#475569' }} />
      </div>
      <div className="p-6">{children}</div>
    </div>
  );

  if (kind === 'map') {
    return (
      <Frame>
        <div className="relative h-64 rounded-xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #0B2A4D 0%, #051428 100%)' }}>
          {/* Aerial-style mock with hotspots */}
          <svg viewBox="0 0 320 240" className="w-full h-full opacity-60">
            <defs>
              <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(56,189,248,0.15)" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="320" height="240" fill="url(#grid)" />
            <path d="M 40 80 L 120 60 L 200 90 L 270 70 L 290 140 L 230 180 L 110 200 L 50 160 Z" fill="rgba(56,189,248,0.08)" stroke="rgba(56,189,248,0.4)" strokeWidth="1.5" />
          </svg>
          {[
            { x: '20%', y: '30%', color: '#22C55E' },
            { x: '55%', y: '45%', color: '#F59E0B' },
            { x: '70%', y: '70%', color: '#EF4444' },
            { x: '35%', y: '70%', color: '#38BDF8' },
          ].map((h, i) => (
            <div key={i} className="absolute w-4 h-4 rounded-full" style={{
              left: h.x, top: h.y, background: h.color, boxShadow: `0 0 0 6px ${h.color}30, 0 0 0 12px ${h.color}15`, animation: `pulse-${i} 2.5s ease-in-out ${i * 0.3}s infinite`,
            }} />
          ))}
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2 text-[9px] uppercase tracking-wider font-bold" style={{ color: '#7DD3FC' }}>
          <Tag>Reconstruction</Tag><Tag>Crucial</Tag><Tag>Requested</Tag>
        </div>
        <style>{`@keyframes pulse-0 { 0%,100%{transform:scale(1)} 50%{transform:scale(1.3)} }
        @keyframes pulse-1 { 0%,100%{transform:scale(1)} 50%{transform:scale(1.3)} }
        @keyframes pulse-2 { 0%,100%{transform:scale(1)} 50%{transform:scale(1.3)} }
        @keyframes pulse-3 { 0%,100%{transform:scale(1)} 50%{transform:scale(1.3)} }`}</style>
      </Frame>
    );
  }

  if (kind === 'roadmap') {
    const phases = [
      { n: 'Phase 01', l: 'Strategic Planning', c: '#F59E0B', p: 100 },
      { n: 'Phase 02', l: 'Reconstruction', c: '#84CC16', p: 72 },
      { n: 'Phase 03', l: 'Operational Framework', c: '#14B8A6', p: 38 },
      { n: 'Phase 04', l: 'Brand & Experience', c: '#3B82F6', p: 12 },
      { n: 'Phase 05', l: 'Launch Sequence', c: '#8B5CF6', p: 0 },
    ];
    return (
      <Frame>
        <p className="text-[10px] font-bold tracking-wider uppercase mb-3" style={{ color: '#7DD3FC' }}>Reconstruction Timeline</p>
        <div className="space-y-2">
          {phases.map((ph) => (
            <div key={ph.n}>
              <div className="flex items-center justify-between text-[10px]" style={{ color: '#CBD5E1' }}>
                <span><strong style={{ color: 'white' }}>{ph.n}</strong> — {ph.l}</span>
                <span style={{ color: ph.c }}>{ph.p}%</span>
              </div>
              <div className="mt-1 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <div className="h-full rounded-full transition-all" style={{ width: `${ph.p}%`, background: ph.c, boxShadow: `0 0 12px ${ph.c}80` }} />
              </div>
            </div>
          ))}
        </div>
      </Frame>
    );
  }

  if (kind === 'departments') {
    const depts = ['Housekeeping', 'Front Desk', 'Spa', 'Security', 'F&B', 'Sales', 'Laundry', 'Entertainment'];
    return (
      <Frame>
        <p className="text-[10px] font-bold tracking-wider uppercase mb-3" style={{ color: '#7DD3FC' }}>Departments</p>
        <div className="grid grid-cols-2 gap-2">
          {depts.map((d, i) => (
            <div key={d} className="rounded-lg p-3 text-xs" style={{ background: i % 2 ? 'rgba(56,189,248,0.05)' : 'rgba(255,255,255,0.03)', border: '1px solid rgba(125,211,252,0.10)', color: 'white' }}>
              <p className="font-bold">{d}</p>
              <p className="text-[10px] mt-0.5" style={{ color: '#94A3B8' }}>{4 + (i % 6)} team · AI schedule ready</p>
            </div>
          ))}
        </div>
      </Frame>
    );
  }

  if (kind === 'fnb') {
    const r = [
      { n: 'Tree Palms', c: '#10B981' },
      { n: 'Coco Café', c: '#F59E0B' },
      { n: 'Sunset Grill', c: '#EF4444' },
      { n: 'Atrium Bar', c: '#3B82F6' },
      { n: 'Ocean Sushi', c: '#8B5CF6' },
      { n: 'Reef Buffet', c: '#14B8A6' },
    ];
    return (
      <Frame>
        <p className="text-[10px] font-bold tracking-wider uppercase mb-3" style={{ color: '#7DD3FC' }}>F&B Concepts · 11 Outlets</p>
        <div className="grid grid-cols-3 gap-2">
          {r.map((x) => (
            <div key={x.n} className="rounded-lg p-3 text-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(125,211,252,0.12)' }}>
              <div className="w-8 h-8 mx-auto rounded-lg mb-2" style={{ background: x.c, boxShadow: `0 4px 12px ${x.c}60` }} />
              <p className="text-[10px] font-bold" style={{ color: 'white' }}>{x.n}</p>
            </div>
          ))}
        </div>
      </Frame>
    );
  }

  if (kind === 'finance') {
    return (
      <Frame>
        <p className="text-[10px] font-bold tracking-wider uppercase mb-3" style={{ color: '#7DD3FC' }}>Property Budget · YTD</p>
        <div className="space-y-3">
          {[
            { l: 'FF&E', v: '$2.4M', p: 65, c: '#22C55E' },
            { l: 'Construction', v: '$8.7M', p: 84, c: '#3B82F6' },
            { l: 'Pre-opening', v: '$890K', p: 32, c: '#F59E0B' },
            { l: 'Tech & Systems', v: '$420K', p: 71, c: '#8B5CF6' },
          ].map((b) => (
            <div key={b.l} className="rounded-lg p-3" style={{ background: 'rgba(255,255,255,0.03)' }}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-bold" style={{ color: 'white' }}>{b.l}</span>
                <span className="text-xs tabular-nums" style={{ color: b.c }}>{b.v}</span>
              </div>
              <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <div className="h-full" style={{ width: `${b.p}%`, background: b.c }} />
              </div>
            </div>
          ))}
        </div>
      </Frame>
    );
  }

  if (kind === 'search') {
    return (
      <Frame>
        <div className="rounded-lg px-4 py-3 mb-3 flex items-center gap-2" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(125,211,252,0.20)' }}>
          <Search className="w-4 h-4" style={{ color: '#7DD3FC' }} />
          <span className="text-xs" style={{ color: 'white' }}>What's overdue this quarter?</span>
        </div>
        <p className="text-[10px] font-bold tracking-wider uppercase mb-2" style={{ color: '#7DD3FC' }}>4 results</p>
        {[
          'Atrium structural repair — 12 days overdue',
          'Spa millwork delivery — 4 days overdue',
          'Pool deck contractor selection — 1 day overdue',
          'Permit renewal · Insurance Block C',
        ].map((line, i) => (
          <div key={i} className="rounded-lg p-2.5 mb-1.5 text-xs flex items-center gap-2" style={{ background: 'rgba(255,255,255,0.03)', color: '#CBD5E1' }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#EF4444' }} />
            {line}
          </div>
        ))}
      </Frame>
    );
  }

  if (kind === 'renders') {
    return (
      <Frame>
        <p className="text-[10px] font-bold tracking-wider uppercase mb-3" style={{ color: '#7DD3FC' }}>AI Render Studio</p>
        <div className="grid grid-cols-2 gap-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="aspect-square rounded-lg relative overflow-hidden" style={{
              background: `linear-gradient(135deg, ${['#0EA5E9', '#8B5CF6', '#F59E0B', '#10B981'][i]}40 0%, #0B1830 100%)`,
              border: '1px solid rgba(125,211,252,0.12)',
            }}>
              <div className="absolute inset-0 flex items-center justify-center">
                <Wand2 className="w-8 h-8 opacity-30" style={{ color: 'white' }} />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 px-3 py-2 rounded-lg text-[10px]" style={{ background: 'rgba(56,189,248,0.08)', color: '#7DD3FC' }}>
          ✨ Generating render 3 of 4 · GPT Image 1
        </div>
      </Frame>
    );
  }

  if (kind === 'assistant') {
    return (
      <Frame>
        <div className="space-y-2">
          <Bubble side="left">What's the status of the Atrium phase?</Bubble>
          <Bubble side="right" tint>72% complete · 4 milestones remaining · 2 overdue. Want me to draft an owner update?</Bubble>
          <Bubble side="left">Yes — formal tone, send via email.</Bubble>
          <Bubble side="right" tint>Drafted. Subject: "Atrium phase update — Week 28". Ready to send to ownership.</Bubble>
        </div>
        <div className="mt-3 rounded-lg px-3 py-2 text-[10px] flex items-center gap-2" style={{ background: 'rgba(56,189,248,0.08)', color: '#7DD3FC' }}>
          <Sparkles className="w-3 h-3" /> Powered by your selected AI engine
        </div>
      </Frame>
    );
  }

  if (kind === 'ose') {
    return (
      <Frame>
        <div className="rounded-lg mb-3 px-3 py-3 flex items-center gap-3" style={{ background: 'rgba(56,189,248,0.06)', border: '1px dashed rgba(125,211,252,0.30)' }}>
          <Camera className="w-5 h-5" style={{ color: '#7DD3FC' }} />
          <div>
            <p className="text-xs font-bold" style={{ color: 'white' }}>Photo captured · OCR running</p>
            <p className="text-[10px]" style={{ color: '#94A3B8' }}>23 line items detected</p>
          </div>
        </div>
        {['Bath towel · 200 ct · $14.50', 'Decorative pillow · 80 ct · $42.00', 'Lobby vase · 12 ct · $189.00'].map((line, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 text-[10px] py-1.5 border-b" style={{ borderColor: 'rgba(125,211,252,0.08)', color: '#CBD5E1' }}>
            <span className="col-span-1" style={{ color: '#7DD3FC' }}>{i + 1}</span>
            <span className="col-span-11">{line}</span>
          </div>
        ))}
      </Frame>
    );
  }

  if (kind === 'rbac') {
    return (
      <Frame>
        <p className="text-[10px] font-bold tracking-wider uppercase mb-3" style={{ color: '#7DD3FC' }}>Change Request · #CR-2026-0173</p>
        <div className="rounded-lg p-3 mb-3" style={{ background: 'rgba(255,255,255,0.03)' }}>
          <p className="text-xs font-bold" style={{ color: 'white' }}>Update Atrium Lobby ROI</p>
          <p className="text-[10px] mt-0.5" style={{ color: '#94A3B8' }}>Submitted by: editor@property.com · 4 fields changed</p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-lg py-3 text-center text-xs font-bold transition-all hover:-translate-y-0.5" style={{ background: 'rgba(34,197,94,0.15)', color: '#86EFAC', border: '1px solid rgba(34,197,94,0.30)' }}>✓ Approve</div>
          <div className="rounded-lg py-3 text-center text-xs font-bold transition-all hover:-translate-y-0.5" style={{ background: 'rgba(239,68,68,0.10)', color: '#FCA5A5', border: '1px solid rgba(239,68,68,0.25)' }}>× Reject</div>
        </div>
      </Frame>
    );
  }

  return null;
}

function Tag({ children }) {
  return <span className="rounded-full px-2 py-1 text-center" style={{ background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.20)' }}>{children}</span>;
}

function Bubble({ children, side, tint }) {
  return (
    <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-xs ${side === 'right' ? 'ml-auto' : ''}`}
      style={{
        background: tint ? 'linear-gradient(135deg, rgba(56,189,248,0.18) 0%, rgba(37,99,235,0.10) 100%)' : 'rgba(255,255,255,0.05)',
        color: tint ? '#E0F2FE' : '#CBD5E1',
        border: tint ? '1px solid rgba(125,211,252,0.30)' : '1px solid rgba(255,255,255,0.06)',
      }}>
      {children}
    </div>
  );
}

function GalleryCard({ item, index }) {
  return (
    <div
      data-testid={`gallery-${index}`}
      className="group relative rounded-2xl overflow-hidden transition-all hover:-translate-y-1"
      style={{
        background: 'linear-gradient(180deg, #0B1830 0%, #06101F 100%)',
        border: '1px solid rgba(125,211,252,0.15)',
        boxShadow: '0 16px 48px rgba(56,189,248,0.10)',
      }}
    >
      <div className="aspect-[16/10] overflow-hidden relative">
        <img
          src={item.src}
          alt={item.caption}
          loading="lazy"
          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[9px] font-bold tracking-widest uppercase"
          style={{ background: 'rgba(2,6,15,0.7)', backdropFilter: 'blur(8px)', color: '#7DD3FC', border: '1px solid rgba(125,211,252,0.25)' }}>
          {item.tag}
        </div>
      </div>
      <div className="px-4 py-3.5" style={{ borderTop: '1px solid rgba(125,211,252,0.10)' }}>
        <p className="text-xs leading-relaxed" style={{ color: '#CBD5E1', fontFamily: 'Manrope' }}>{item.caption}</p>
      </div>
    </div>
  );
}
